import { type Ref, computed, ref } from 'vue'

import type {
  AlgoliaLink,
  AlgoliaProduct,
} from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia'
import type { SearchBarConfiguration } from '@backmarket/http-api/src/api-specs-search-reco/search/searchConfiguration'
import { useAlgoliaSearchOptions } from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import { useTestChallenge } from '@backmarket/nuxt-layer-algolia/useRedirectToTestChallenge'
import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import algoliasearch from 'algoliasearch'

import {
  type Link,
  algoliaHitToLink,
  algoliaHitToProduct,
} from '~/scopes/search/algolia/algoliaFunctions'

import {
  MAX_PRODUCTS_HITS,
  MAX_SUGGESTIONS_HITS,
} from '../../SearchBar.constants'

export interface UseAlgoliaSearchParams {
  query: Ref<string>
  configuration: Ref<SearchBarConfiguration | null>
}

export function useAlgoliaSearch({
  query,
  configuration,
}: UseAlgoliaSearchParams) {
  const productsHits = ref<Product[]>([])
  const suggestionsHits = ref<Link[]>([])
  const isFetchingProductAndSuggestions = ref(false)

  const logger = useLogger()
  const algoliaSearchOption = useAlgoliaSearchOptions('search')
  const { redirectToTestChallengePage, isTestChallengeError } =
    useTestChallenge()

  const algoliaIndexProducts = computed(() => {
    if (!configuration.value) return null

    return algoliasearch('dummy_app_id', 'dummy_key', {
      ...algoliaSearchOption,
    }).initIndex(configuration.value.backboxWinners.indexes.active.name)
  })

  const algoliaIndexSuggestions = computed(() => {
    if (!configuration.value) return null

    return algoliasearch('dummy_app_id', 'dummy_key', {
      ...algoliaSearchOption,
    }).initIndex(configuration.value.landingPages.indexes.active.name)
  })

  const searchProducts = async () => {
    if (!algoliaIndexProducts.value || !query.value) return
    try {
      const resultProducts =
        await algoliaIndexProducts.value.search<AlgoliaProduct>(query.value, {
          filters: configuration.value?.backboxWinners.complexFilter,
          hitsPerPage: MAX_PRODUCTS_HITS,
        })
      productsHits.value = resultProducts.hits.map((hit) => {
        return algoliaHitToProduct(hit)
      })
    } catch (error) {
      logger.info('[S&R] Impossible trigger Search Bar product search', {
        type: 'API_REQUEST_ERROR',
        owners: ['bot-squad-search-and-recommendation-front'],
        cause: error,
      })

      if (isTestChallengeError(error)) {
        redirectToTestChallengePage()
      }
    }
  }

  const searchSuggestions = async () => {
    if (!algoliaIndexSuggestions.value || !query.value) return
    try {
      const resultSuggestions =
        await algoliaIndexSuggestions.value.search<AlgoliaLink>(query.value, {
          highlightPreTag: '<span class="body-1-bold text-static-default-hi">',
          highlightPostTag: '</span>',
          clickAnalytics: true,
          filters: configuration.value?.landingPages.complexFilter,
          hitsPerPage: MAX_SUGGESTIONS_HITS,
        })
      suggestionsHits.value = resultSuggestions.hits
        .filter(
          (hit) =>
            !(
              hit.link.type === 'internal' &&
              hit.link.params.slugV2 === undefined
            ),
        )
        .map((hit) => {
          return algoliaHitToLink(hit)
        })
    } catch (error) {
      logger.info('[S&R] Impossible trigger Search Bar suggestions search', {
        type: 'API_REQUEST_ERROR',
        owners: ['bot-squad-search-and-recommendation-front'],
        cause: error,
      })

      if (isTestChallengeError(error)) {
        redirectToTestChallengePage()
      }
    }
  }

  const searchProductsAndSuggestions = async () => {
    isFetchingProductAndSuggestions.value = true
    try {
      await Promise.all([searchProducts(), searchSuggestions()])
    } catch (error) {
      logger.info('[S&R] Impossible trigger Search Bar search', {
        type: 'API_REQUEST_ERROR',
        owners: ['bot-squad-search-and-recommendation-front'],
        cause: error,
      })
    } finally {
      isFetchingProductAndSuggestions.value = false
    }
  }

  return {
    productsHits,
    suggestionsHits,
    isFetchingProductAndSuggestions,
    searchProductsAndSuggestions,
  }
}
